/**
 * Publishes a payload-free event after navigation between top-level app areas.
 *
 * Most areas are identified by the first URL segment. Partner and lender URLs
 * share a role prefix, so their second segment identifies the area instead.
 * Deeper segments are ignored so steps within signup, company, application,
 * and other flows do not inflate Meta page-view counts.
 *
 * The root role dispatcher, the company-switching waypoint, and application
 * flow routes are not screens themselves. Passing through one retains the last
 * stable area, allowing the eventual destination to produce at most one event.
 *
 * The app emits this internal event in every environment. Builds configured
 * with a GTM container forward it as an equally payload-free data-layer event;
 * builds without a container ID send nothing externally.
 */
import type { AnyRouter } from "@tanstack/react-router"

export const SCREEN_CHANGE_EVENT = "boundless:screen-change"
export const SCREEN_CHANGE_DATA_LAYER_EVENT = "boundless_screen_change"

const ROLE_PATH_PREFIXES = new Set(["lender", "partners"])
const TRANSIENT_PATHS = new Set(["/", "/resume"])
const APPLICATION_FLOW_ROUTE_ID = "/_dashboard/_application-flow"

function topLevelArea(pathname: string): string | undefined {
  if (TRANSIENT_PATHS.has(pathname)) {
    return
  }

  const [firstSegment, secondSegment] = pathname.split("/").filter(Boolean)
  if (!firstSegment) {
    return
  }

  if (ROLE_PATH_PREFIXES.has(firstSegment) && secondSegment) {
    return `/${firstSegment}/${secondSegment}`
  }

  return `/${firstSegment}`
}

/** Registers the application-lifetime publisher for top-level screen changes. */
export function registerScreenChangeEvents(router: AnyRouter): () => void {
  let currentArea: string | undefined

  const resolvedArea = (pathname: string): string | undefined => {
    const isApplicationFlow = router.state.matches.some(
      ({ routeId }) => routeId === APPLICATION_FLOW_ROUTE_ID,
    )

    return isApplicationFlow ? undefined : topLevelArea(pathname)
  }

  return router.subscribe(
    "onResolved",
    ({ fromLocation, toLocation, pathChanged }) => {
      const nextArea = resolvedArea(toLocation.pathname)
      if (!nextArea) {
        return
      }

      const previousArea =
        currentArea ?? topLevelArea(fromLocation?.pathname ?? "")
      currentArea = nextArea

      // GTM handles the initial document page view. Waiting for an actual path
      // change between two stable areas prevents that load from being doubled.
      if (
        !fromLocation ||
        !pathChanged ||
        !previousArea ||
        previousArea === nextArea
      ) {
        return
      }

      document.dispatchEvent(new Event(SCREEN_CHANGE_EVENT))
    },
  )
}
